import { App, Form, Input, ModalFuncProps } from 'antd';
import React, { useRef } from 'react';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import { EditOutlined } from '@ant-design/icons';
import { v4 as uuidV4 } from 'uuid';
import { useApolloClient } from '@apollo/client';
import { DebounceSelect, FlexListTemplate } from '../../../../../components';
import {
  ChangeFolderTypeInput,
  FolderTypeDtoFilterInput,
  GetManagerFolderTypeListDocument,
  GetManagerFolderTypeListQuery,
  ManagerFolderTreeListItemFragment,
  useChangeFolderTypeMutation,
} from '../../../../../services/apolloGraphqlAppApiClient/models/types';
import { useFoldersTreeStoreContext } from '../FoldersTreeStoreProvider';

export interface IUseChangeFolderTypeActionProps {
  folderData: ManagerFolderTreeListItemFragment;
}

export interface IUseChangeFolderTypeActionResult {
  changeFolderTypeAction: ItemType;
}

export const useChangeFolderTypeAction = (props: IUseChangeFolderTypeActionProps): IUseChangeFolderTypeActionResult => {
  const {
    modal: { confirm },
    notification: { info },
  } = App.useApp();
  const { folderData } = props;
  const apolloGraphqlAppClient = useApolloClient();
  const { refetchFolders } = useFoldersTreeStoreContext();
  const menuItemKey = `ChangeFolderTypeAction-${folderData.id}`;
  const infoKey = uuidV4();
  const modalRef = useRef<ReturnType<typeof confirm>>();

  const visible = folderData.title !== 'Unpublished';

  const [changeFolderTypeForm] = Form.useForm<ChangeFolderTypeInput>();

  const [changeFolderType] = useChangeFolderTypeMutation({
    onCompleted: (result) => {
      refetchFolders();
      info({
        placement: 'topRight',
        message: `Change Folder type status: true`,
        description: (
          <FlexListTemplate gap={5} alignItems="left" justifyContent="left" style={{ flexDirection: 'column' }}>
            {/* info */}
          </FlexListTemplate>
        ),
        key: infoKey,
        duration: 7,
      });
    },
    onError: (e) => {
      return e;
    },
  });

  const handleOk: ModalFuncProps['onOk'] = () => {
    changeFolderTypeForm
      .validateFields()
      .then((values) => {
        return changeFolderType({
          variables: {
            input: { ...values },
          },
        });
      })
      .then((res) => {
        // changeFolderTypeForm.resetFields();
        if (modalRef.current) {
          modalRef.current.destroy();
        }
      })
      .catch((e) => {
        return e;
      });
  };

  const hadleCancel = () => {
    changeFolderTypeForm.resetFields();
    if (modalRef.current) {
      modalRef.current.destroy();
    }
  };

  return {
    changeFolderTypeAction: visible
      ? {
          key: menuItemKey,
          icon: <EditOutlined />,
          label: 'Change Type',
          onClick: (menuInfo) => {
            menuInfo.domEvent.preventDefault();
            menuInfo.domEvent.stopPropagation();
            modalRef.current = confirm({
              title: `Change ${folderData.title} folder type!`,
              onOk: handleOk,
              onCancel: hadleCancel,
              okText: 'Change Type',
              content: (
                <Form<ChangeFolderTypeInput>
                  initialValues={{
                    id: folderData.id,
                    type: folderData?.typeId,
                  }}
                  layout="vertical"
                  form={changeFolderTypeForm}
                  name="changeFolderTypeForm_name"
                >
                  <Form.Item name="id" label="Id" hidden>
                    <Input type="textarea" />
                  </Form.Item>
                  <Form.Item
                    name="type"
                    label="Type"
                    valuePropName="value"
                    rules={[
                      {
                        required: true,
                        message: `Please select folder type!`,
                      },
                    ]}
                  >
                    <DebounceSelect
                      style={{ width: '100%' }}
                      placeholder="Select folder type"
                      fetchCallback={async (value) => {
                        try {
                          const response = await apolloGraphqlAppClient.query<
                            GetManagerFolderTypeListQuery,
                            { where: FolderTypeDtoFilterInput }
                          >({
                            query: GetManagerFolderTypeListDocument,
                            variables: {
                              where: {
                                id: { neq: 'root' },
                                ...(value
                                  ? {
                                      or: [{ title: { contains: value } }],
                                    }
                                  : {}),
                              },
                            },
                          });
                          const {
                            data: { folderTypes },
                          } = response;
                          return folderTypes.map((type) => ({
                            label: `${type.title}${
                              type.allowedTypes.length > 0 ? ` - [ ${type.allowedTypes.join(', ')} ]` : ''
                            }`,
                            value: type.id,
                          }));
                        } catch (e) {
                          return [];
                        }
                      }}
                    />
                  </Form.Item>
                </Form>
              ),
            });
          },
        }
      : null,
  };
};
